/**
 * Token Storage Utility
 * 
 * Manages secure storage and retrieval of authentication tokens.
 * Stores access tokens (short-lived) and refresh tokens (long-lived) in localStorage.
 */

const TOKEN_KEYS = {
  ACCESS_TOKEN: 'auth_access_token',
  REFRESH_TOKEN: 'auth_refresh_token',
  TOKEN_EXPIRY: 'auth_token_expiry'
};

class TokenStorage {
  /**
   * Store authentication tokens
   * @param {string} accessToken - JWT access token
   * @param {string} refreshToken - Refresh token for obtaining new access tokens
   * @param {number} expiresIn - Token expiry time in seconds
   */
  static setTokens(accessToken, refreshToken, expiresIn) {
    try {
      localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
      localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
      
      // Calculate and store expiry timestamp (current time + expiresIn - 60 second buffer)
      const expiryTime = Date.now() + (expiresIn * 1000) - 60000;
      localStorage.setItem(TOKEN_KEYS.TOKEN_EXPIRY, expiryTime.toString());
      
      console.log('[TOKEN STORAGE] ✅ Tokens stored successfully');
      console.log('[TOKEN STORAGE] Access token expires in:', expiresIn, 'seconds');
    } catch (error) {
      console.error('[TOKEN STORAGE] ❌ Error storing tokens:', error);
    }
  }

  /**
   * Get the current access token
   * @returns {string|null} Access token or null if not found
   */
  static getAccessToken() {
    try {
      return localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
    } catch (error) {
      console.error('[TOKEN STORAGE] ❌ Error getting access token:', error);
      return null;
    }
  }

  /**
   * Get the current refresh token
   * @returns {string|null} Refresh token or null if not found
   */
  static getRefreshToken() {
    try {
      return localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
    } catch (error) {
      console.error('[TOKEN STORAGE] ❌ Error getting refresh token:', error);
      return null;
    }
  }

  /**
   * Check if the access token is expired or about to expire
   * @returns {boolean} True if token is expired or will expire soon
   */
  static isTokenExpired() {
    try {
      const expiryTime = localStorage.getItem(TOKEN_KEYS.TOKEN_EXPIRY);
      if (!expiryTime) {
        return true;
      }
      
      const now = Date.now();
      const expiry = parseInt(expiryTime, 10);
      
      // Token is considered expired if it expires within the next minute
      return now >= expiry;
    } catch (error) {
      console.error('[TOKEN STORAGE] ❌ Error checking token expiry:', error);
      return true;
    }
  }

  /**
   * Get time until token expiry in milliseconds
   * @returns {number} Milliseconds until expiry (0 if expired)
   */
  static getTimeUntilExpiry() {
    try {
      const expiryTime = localStorage.getItem(TOKEN_KEYS.TOKEN_EXPIRY);
      if (!expiryTime) {
        return 0;
      }
      
      const now = Date.now();
      const expiry = parseInt(expiryTime, 10);
      const timeRemaining = expiry - now;
      
      return Math.max(0, timeRemaining);
    } catch (error) {
      console.error('[TOKEN STORAGE] ❌ Error calculating time until expiry:', error);
      return 0;
    }
  }

  /**
   * Clear all stored tokens (used during logout)
   */
  static clearTokens() {
    try {
      localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(TOKEN_KEYS.TOKEN_EXPIRY);
      console.log('[TOKEN STORAGE] ✅ Tokens cleared');
    } catch (error) {
      console.error('[TOKEN STORAGE] ❌ Error clearing tokens:', error);
    }
  }

  /**
   * Check if user has valid tokens
   * @returns {boolean} True if both access and refresh tokens exist
   */
  static hasTokens() {
    return !!this.getAccessToken() && !!this.getRefreshToken();
  }

  /**
   * Update only the access token (used after refresh)
   * @param {string} accessToken - New JWT access token
   * @param {number} expiresIn - Token expiry time in seconds
   */
  static updateAccessToken(accessToken, expiresIn) {
    try {
      localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
      
      // Calculate and store new expiry timestamp
      const expiryTime = Date.now() + (expiresIn * 1000) - 60000;
      localStorage.setItem(TOKEN_KEYS.TOKEN_EXPIRY, expiryTime.toString());
      
      console.log('[TOKEN STORAGE] ✅ Access token updated');
    } catch (error) {
      console.error('[TOKEN STORAGE] ❌ Error updating access token:', error);
    }
  }

  /**
   * Update the refresh token (used after token rotation)
   * @param {string} refreshToken - New refresh token
   */
  static updateRefreshToken(refreshToken) {
    try {
      localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
      console.log('[TOKEN STORAGE] ✅ Refresh token updated');
    } catch (error) {
      console.error('[TOKEN STORAGE] ❌ Error updating refresh token:', error);
    }
  }
}

export default TokenStorage;